import { getFirestore, collection, addDoc, query, where, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Initialize Firestore from the Firebase app
const db = getFirestore();

// Function to bookmark an article
export const bookmarkArticle = async (articleId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be logged in to bookmark articles');
    }
    
    // Check if bookmark already exists
    const bookmarksRef = collection(db, 'bookmarks');
    const q = query(bookmarksRef, 
      where('userId', '==', user.uid),
      where('articleId', '==', articleId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // Get article data for bookmarking
      const articleRef = doc(db, 'articles', articleId);
      const articleDoc = await getDoc(articleRef);
      
      if (!articleDoc.exists()) {
        throw new Error('Article not found');
      }
      
      const articleData = articleDoc.data();
      
      // Create bookmark document
      await addDoc(collection(db, 'bookmarks'), {
        userId: user.uid,
        articleId: articleId,
        articleTitle: articleData.title || '',
        articleAuthor: articleData.author || '',
        authorImage: articleData.authorImage || null,
        createdAt: serverTimestamp()
      });
      
      return { success: true, message: 'Article bookmarked successfully' };
    }
    
    // Bookmark already exists
    return { success: true, message: 'Article already bookmarked' };
  } catch (error) {
    console.error('Error bookmarking article:', error);
    throw error;
  }
};

// Function to remove bookmark from an article
export const removeBookmark = async (articleId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be logged in to remove bookmarks');
    }
    
    // Find bookmark document
    const bookmarksRef = collection(db, 'bookmarks');
    const q = query(bookmarksRef, 
      where('userId', '==', user.uid),
      where('articleId', '==', articleId)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Delete bookmark document
      await deleteDoc(querySnapshot.docs[0].ref);
    }
    
    return { success: true, message: 'Bookmark removed successfully' };
  } catch (error) {
    console.error('Error removing bookmark:', error);
    throw error;
  }
};

// Function to check if an article is bookmarked by the current user
export const isArticleBookmarked = async (articleId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return false;
    }
    
    // Find bookmark document
    const bookmarksRef = collection(db, 'bookmarks');
    const q = query(bookmarksRef, 
      where('userId', '==', user.uid),
      where('articleId', '==', articleId)
    );
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking bookmark status:', error);
    return false;
  }
};

// Function to get all bookmarked articles for the current user
export const getBookmarkedArticles = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be logged in to view bookmarks');
    }
    
    // Find all bookmarks for this user
    const bookmarksRef = collection(db, 'bookmarks');
    const q = query(bookmarksRef, where('userId', '==', user.uid));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return [];
    }
    
    // Get all bookmarked articles
    const bookmarkedArticles = [];
    
    for (const bookmark of querySnapshot.docs) {
      const bookmarkData = bookmark.data();
      const articleDoc = await getDoc(doc(db, 'articles', bookmarkData.articleId));
      
      if (articleDoc.exists()) {
        bookmarkedArticles.push({
          id: articleDoc.id,
          ...articleDoc.data(),
          bookmarkId: bookmark.id // Include bookmark ID for deletion reference
        });
      }
    }
    
    return bookmarkedArticles;
  } catch (error) {
    console.error('Error getting bookmarked articles:', error);
    throw error;
  }
};

// Function to share an article (record share event)
export const shareArticle = async (articleId, shareMethod) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Get article reference
    const articleRef = doc(db, 'articles', articleId);
    const articleDoc = await getDoc(articleRef);
    
    if (!articleDoc.exists()) {
      throw new Error('Article not found');
    }
    
    // Record share event
    await addDoc(collection(db, 'shares'), {
      articleId,
      shareMethod,
      userId: user ? user.uid : null,
      timestamp: serverTimestamp()
    });
    
    // Update share count on the article
    const articleData = articleDoc.data();
    const shareCount = articleData.shareCount || 0;
    
    await updateDoc(articleRef, {
      shareCount: shareCount + 1
    });
    
    return { success: true, message: 'Article shared successfully' };
  } catch (error) {
    console.error('Error sharing article:', error);
    throw error;
  }
};

// Function to get share URL for an article
export const getShareUrl = (articleId) => {
  // In a real app, this would be a proper URL to your deployed site
  return `https://mycolu.com/article/${articleId}`;
};

// Function to share article via different platforms
export const shareViaMethod = async (articleId, title, method) => {
  const shareUrl = getShareUrl(articleId);
  
  try {
    switch (method) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${shareUrl}`)}`;
        break;
      case 'copy':
        await navigator.clipboard.writeText(shareUrl);
        break;
      default:
        throw new Error('Invalid share method');
    }
    
    // Record share event
    await shareArticle(articleId, method);
    
    return { success: true, message: `Shared via ${method}` };
  } catch (error) {
    console.error(`Error sharing via ${method}:`, error);
    throw error;
  }
};

export default {
  bookmarkArticle,
  removeBookmark,
  isArticleBookmarked,
  getBookmarkedArticles,
  shareArticle,
  getShareUrl,
  shareViaMethod
};
